// file: pharmakon/client/src/config/app.config.js
/**
 * @fileoverview PHARMAKON — Frontend Application Configuration
 * @description Single source of truth for ALL UI constants, formatters, labels, options.
 *              NEVER hardcode values in components — always import from here.
 *              Mirror of server/src/config/constants.js for the client side.
 */
import { T, BASE_CSS, ICONS } from "./theme.js";
import { fmtMoney } from "./locale.config.js";
import { partsChifa } from "@pharmakon/shared/pricing";
// ─── Environment ──────────────────────────────────────────────────────────────
// Accept either name (VITE_API_URL is canonical; VITE_API_BASE_URL kept for
// back-compat). On Railway, set VITE_API_URL to the server service's public
// URL + "/api/v1".
const RAW_API_URL = (
  import.meta.env.VITE_API_URL ?? "http://localhost:3001/api/v1"
).trim();
export const API_BASE_URL = (
  /^https?:\/\//i.test(RAW_API_URL) ? RAW_API_URL : `https://${RAW_API_URL}`
).replace(/\/+$/, "");
export const APP_NAME = import.meta.env.VITE_APP_NAME ?? "PHARMAKON";
export const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? "1.0.0";
export const BRIDGE_URL =
  import.meta.env.VITE_BRIDGE_URL ?? "http://localhost:3002";

// Server origin (without the /api/v1 suffix) — used to resolve static assets
// like uploaded logos, which the server serves at <origin>/uploads/...
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/v\d+\/?$/, "");

/**
 * Resolve a server asset path (e.g. "/uploads/logos/x.png") to an absolute URL
 * the SPA can load. Pass-through for already-absolute http(s) URLs and empty
 * values. Used for the per-pharmacy logo across the chrome and settings.
 */
export const assetUrl = (p) => {
  if (!p) return "";
  if (/^(https?:|data:|blob:)/i.test(p)) return p;
  return `${API_ORIGIN}${p.startsWith("/") ? "" : "/"}${p}`;
};

// ─── Locale / Currency ────────────────────────────────────────────────────────
export const LOCALE = "fr-DZ";
export const CURRENCY = "DZD";

// ─── Pagination ───────────────────────────────────────────────────────────────
export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
// Server-side hard cap on `query.limit` — every list validator pipes
// `.max(200)` (e.g. server/src/validators/fournisseur.validator.js). Any
// client request above it is a 422, so "load-all" fetches MUST page in
// chunks of at most this size (R3-H28: a limit=500 dropdown fetch 422'd and
// silently emptied every supplier picker).
export const LIST_LIMIT_MAX = 200;
// Safety bound for exhaustive "load-all" pagination loops (dropdowns):
// LIST_LIMIT_MAX × DROPDOWN_MAX_PAGES rows maximum, so a pathological
// `total` can never spin the UI into an unbounded request storm.
export const DROPDOWN_MAX_PAGES = 25;

// ─── Formatters ───────────────────────────────────────────────────────────────
// OP-F-04 — one money formatter: `formatCurrency` is kept for its 31 importers
// and delegates to `fmtMoney` (config/locale.config.js), so both render
// « 1 250,00 DA » and « — » for an unknown value.
export const formatCurrency = (v) => fmtMoney(v);

export const formatCurrencyCompact = (v) =>
  new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: CURRENCY,
    notation: "compact",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(v ?? 0);

export const formatDate = (d) =>
  d
    ? new Intl.DateTimeFormat(LOCALE, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(d))
    : "—";

/**
 * Batch-1 (2026-08) — `hourCycle: "h23"` est OBLIGATOIRE, pas décoratif.
 *
 * `Intl` résout `fr-DZ` sur une horloge de 12 heures : sans ce réglage,
 * `formatDateTime` rend « 21/08/2026 10:26 PM » — un horodatage anglo-saxon au
 * milieu d'une interface française. Le défaut est resté invisible tant que la
 * fonction ne servait qu'à des listes ; il devient inacceptable dès qu'un
 * horodatage entre dans un registre médico-légal (§12 / Q8 : « Saisi
 * manuellement le {date} par {utilisateur} »).
 *
 * `h23` (00–23) et non `h24` (01–24) : minuit s'écrit « 00:00 » en français.
 */
export const formatDateTime = (d) =>
  d
    ? new Intl.DateTimeFormat(LOCALE, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(new Date(d))
    : "—";

export const formatPercent = (v) => `${(v ?? 0).toFixed(2)}%`;

export const formatQuantity = (v) =>
  new Intl.NumberFormat(LOCALE).format(v ?? 0);

// ─── CHIFA Formula — the shared engine's split, the one the server stores ─────
// Cross-module X XMD-02-07: integer centimes, AS + PS = PPA × qte exactly (no float mirror).
export const calcChifa = ({ ppa, tr, tauxPEC, qte }) =>
  partsChifa({ ppa, tr, taux_pec: tauxPEC, quantite: qte });

// ─── SHP Values ───────────────────────────────────────────────────────────────
export const SHP_VALUES = Object.freeze({
  HORS_LISTE: 0.0,
  LISTE_I: 2.5,
  LISTE_II: 1.5,
  TABLEAU_B: 0.0,
});

// ─── ACHAT ────────────────────────────────────────────────────────────────────
export const STATUT_ACHAT_LABELS = Object.freeze({
  BROUILLON: "Brouillon",
  VALIDE: "Validé",
  AVOIR: "Avoir",
  ANNULE: "Annulé",
});

export const STATUT_ACHAT_COLORS = Object.freeze({
  BROUILLON: "bg-amber-100 text-amber-800",
  VALIDE: "bg-green-100 text-green-800",
  AVOIR: "bg-blue-100 text-blue-800",
  ANNULE: "bg-red-100 text-red-700",
});

export const STATUT_ACHAT_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "BROUILLON", label: "Brouillon" },
  { value: "VALIDE", label: "Validé" },
  // Refonte M-10: AVOIR is a retired facture state — kept in the labels above for history only.
  { value: "ANNULE", label: "Annulé" },
];

export const ACHAT_SORT_OPTIONS = [
  { value: "date_facture", label: "Date" },
  { value: "numero_interne", label: "Numéro" },
  { value: "montant_ttc", label: "Montant TTC" },
  { value: "created_at", label: "Créé le" },
];

export const TYPE_PIECE_LABELS = Object.freeze({
  FACTURE: "Facture",
  BON_LIVRAISON: "Bon de Livraison",
  AVOIR: "Avoir",
});

// ─── VENTE ────────────────────────────────────────────────────────────────────
export const STATUT_VENTE_LABELS = Object.freeze({
  EN_COURS: "En cours",
  VALIDEE: "Validée",
  ANNULEE: "Annulée",
  RETOURNEE: "Retournée",
});

export const STATUT_VENTE_COLORS = Object.freeze({
  EN_COURS: "bg-blue-100 text-blue-800",
  VALIDEE: "bg-green-100 text-green-800",
  ANNULEE: "bg-red-100 text-red-700",
  RETOURNEE: "bg-gray-100 text-gray-700",
});

export const TYPE_VENTE_LABELS = Object.freeze({
  LIBRE: "Vente Libre",
  CHIFA: "Tiers Payant",
  SANS_CARTE: "Sans Carte",
  AVOIR: "Avoir",
});

export const TYPE_VENTE_COLORS = Object.freeze({
  LIBRE: "bg-purple-100 text-purple-800",
  // DS-v2: teal → clinical-green family (emerald)
  CHIFA: "bg-emerald-100 text-emerald-800",
  SANS_CARTE: "bg-orange-100 text-orange-800",
  AVOIR: "bg-gray-100 text-gray-700",
});

export const STATUT_VENTE_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "EN_COURS", label: "En cours" },
  { value: "VALIDEE", label: "Validée" },
  { value: "ANNULEE", label: "Annulée" },
  { value: "RETOURNEE", label: "Retournée" },
];

export const TYPE_VENTE_OPTIONS = [
  { value: "", label: "Tous les types" },
  { value: "LIBRE", label: "Vente Libre" },
  { value: "CHIFA", label: "Tiers Payant CHIFA" },
  { value: "SANS_CARTE", label: "Sans Carte" },
];

export const VENTE_SORT_OPTIONS = [
  { value: "date_vente", label: "Date" },
  { value: "numero_interne", label: "Numéro" },
  { value: "montant_total", label: "Montant" },
  { value: "created_at", label: "Créé le" },
];

// ─── STOCK ────────────────────────────────────────────────────────────────────
export const STOCK_ALERT_LABELS = Object.freeze({
  EPUISE: "Épuisé",
  BAS: "Stock bas",
  PEREMPT: "Proche péremption",
  PERIME: "Périmé",
});

export const STOCK_ALERT_COLORS = Object.freeze({
  EPUISE: "bg-red-100 text-red-800",
  BAS: "bg-yellow-100 text-yellow-800",
  PEREMPT: "bg-orange-100 text-orange-800",
  PERIME: "bg-gray-200 text-gray-600",
});

// ─── PRODUIT ──────────────────────────────────────────────────────────────────
// Mirrors the server TypeProduit enum (APPEND-ONLY): the legacy three, the
// Cluster D pair (2026-06), COMPLEMENT_ALIMENTAIRE (Batch 4, 2026-07), then
// ORTHOPEDIE (Batch-1 §8, 2026-08).
// DISPOSITIF is a legacy label-only key kept for old payloads — NOT a DB value.
export const TYPE_PRODUIT_LABELS = Object.freeze({
  MEDICAMENT: "Médicament",
  PARAPHARMACIE: "Parapharmacie",
  COSMETIQUE: "Cosmétique",
  CONSOMMABLE: "Consommable",
  MATERIEL_MEDICAL: "Matériel médical",
  COMPLEMENT_ALIMENTAIRE: "Complément alimentaire",
  // Batch-1 §8 / élément 11 — le type demandé par le commanditaire. Un article
  // orthopédique était jusqu'ici rangé en « Parapharmacie » : introuvable au
  // filtre, et soumis à une péremption obligatoire qui n'a pas de sens pour une
  // attelle. Le libellé ci-dessous alimente TOUS les rendus (achat, fiche,
  // recherche) — il n'y a pas de second endroit à mettre à jour.
  ORTHOPEDIE: "Orthopédie",
  DISPOSITIF: "Dispositif médical",
});

// Refonte WP-68 (A-117) — the type filters and pickers read the officine's catalogue
// (`lib/types-produit.js` `optionsTypes` / `optionsFiltreTypes`), never a hand list here.

export const LISTE_CLASSIFICATION_OPTIONS = [
  { value: "HORS_LISTE", label: "Hors liste — SHP 0 DA" },
  { value: "LISTE_II", label: "Liste II — SHP 1.50 DA" },
  { value: "LISTE_I", label: "Liste I — SHP 2.50 DA" },
  { value: "TABLEAU_B", label: "Tableau B (stupéfiants)" },
];

// ─── CAISSE ───────────────────────────────────────────────────────────────────
export const STATUT_CAISSE_LABELS = Object.freeze({
  OUVERTE: "Ouverte",
  CLOTUREE: "Clôturée",
});

export const STATUT_CAISSE_COLORS = Object.freeze({
  OUVERTE: "bg-green-100 text-green-800",
  CLOTUREE: "bg-gray-100 text-gray-700",
});

// ─── USER ROLES ───────────────────────────────────────────────────────────────
export const ROLE_LABELS = Object.freeze({
  PROPRIETAIRE: "Propriétaire",
  MANAGER: "Manager",
  CAISSIER: "Caissier(ère)",
  FOURNISSEUR: "Fournisseur",
  LABORATOIRE: "Laboratoire",
  ADMIN_PHARMAKON: "Admin PHARMAKON",
});

export const ROLE_COLORS = Object.freeze({
  PROPRIETAIRE: "bg-green-100 text-green-800",
  MANAGER: "bg-blue-100 text-blue-800",
  CAISSIER: "bg-purple-100 text-purple-800",
  FOURNISSEUR: "bg-orange-100 text-orange-800",
  LABORATOIRE: "bg-emerald-100 text-emerald-800",
  ADMIN_PHARMAKON: "bg-red-100 text-red-800",
});

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
export const DATE_RANGE_OPTIONS = [
  { value: "today", label: "Aujourd'hui" },
  { value: "week", label: "7 derniers jours" },
  { value: "month", label: "Mois courant" },
  { value: "last_month", label: "Mois précédent" },
  { value: "quarter", label: "Trimestre courant" },
  { value: "year", label: "Année courante" },
  { value: "custom", label: "Période personnalisée" },
];

export const getDateRange = (preset) => {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const fmt = (d) => d.toISOString().slice(0, 10);
  switch (preset) {
    case "today":
      return { debut: today, fin: today };
    case "week": {
      const w = new Date(now);
      w.setDate(w.getDate() - 6);
      return { debut: fmt(w), fin: today };
    }
    case "month": {
      const y = now.getFullYear(),
        m = String(now.getMonth() + 1).padStart(2, "0");
      return {
        debut: `${y}-${m}-01`,
        fin: `${y}-${m}-${new Date(y, now.getMonth() + 1, 0).getDate()}`,
      };
    }
    case "last_month": {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const y = d.getFullYear(),
        m = String(d.getMonth() + 1).padStart(2, "0");
      return {
        debut: `${y}-${m}-01`,
        fin: `${y}-${m}-${new Date(y, d.getMonth() + 1, 0).getDate()}`,
      };
    }
    case "quarter": {
      const q = Math.floor(now.getMonth() / 3);
      return {
        debut: fmt(new Date(now.getFullYear(), q * 3, 1)),
        fin: fmt(new Date(now.getFullYear(), q * 3 + 3, 0)),
      };
    }
    case "year": {
      const y = now.getFullYear();
      return { debut: `${y}-01-01`, fin: `${y}-12-31` };
    }
    // Rolling N-day windows ending today (Propriétaire dashboard selector).
    case "7j":
    case "30j":
    case "90j": {
      const days = { "7j": 7, "30j": 30, "90j": 90 }[preset];
      const start = new Date(now);
      start.setDate(start.getDate() - (days - 1));
      return { debut: fmt(start), fin: today };
    }
    default:
      return { debut: today, fin: today };
  }
};

// ─── Chart Colors ─────────────────────────────────────────────────────────────
export const CHART_COLORS = Object.freeze({
  primary: "#16a34a",
  // DS-v2: secondary repointed teal (#0d9488) → clinical-green family (#059669)
  secondary: "#059669",
  accent: "#7c3aed",
  warning: "#d97706",
  danger: "#dc2626",
  neutral: "#6b7280",
  series: ["#16a34a", "#059669", "#7c3aed", "#d97706", "#dc2626", "#2563eb"],
});

// ─── UI Messages ──────────────────────────────────────────────────────────────
export const UI_MESSAGES = Object.freeze({
  ACHAT: {
    CREATE_SUCCESS: "Achat créé avec succès",
    CREATE_ERROR: "Erreur lors de la création de l'achat",
    UPDATE_SUCCESS: "Achat mis à jour",
    VALIDATE_SUCCESS: "Achat validé — stock mis à jour ✓",
    VALIDATE_ERROR: "Erreur lors de la validation",
    DELETE_SUCCESS: "Achat supprimé",
    DELETE_ERROR: "Erreur lors de la suppression",
    DIFFERENCE_NON_NULLE: "Différence non nulle — corrigez avant de valider",
  },
  VENTE: {
    CREATE_SUCCESS: "Vente créée",
    VALIDATE_SUCCESS: "Vente validée — stock déduit (FEFO) ✓",
    VALIDATE_ERROR: "Erreur lors de la validation",
    CANCEL_SUCCESS: "Vente annulée — stock restauré",
    CANCEL_ERROR: "Erreur lors de l'annulation",
    STOCK_INSUFFISANT: "Stock insuffisant pour ce produit",
  },
  PRODUIT: {
    CREATE_SUCCESS: "Produit ajouté au catalogue",
    UPDATE_SUCCESS: "Produit mis à jour",
    DELETE_SUCCESS: "Produit supprimé du catalogue",
    IMPORT_SUCCESS: "Données importées depuis la base CNAS",
    IMPORT_ERROR: "N° enregistrement introuvable dans la base CNAS",
  },
  FOURNISSEUR: {
    CREATE_SUCCESS: "Fournisseur créé",
    UPDATE_SUCCESS: "Fournisseur mis à jour",
    DELETE_SUCCESS: "Fournisseur supprimé",
  },
  GENERIC: {
    LOADING: "Chargement…",
    SAVE_SUCCESS: "Enregistré avec succès",
    SAVE_ERROR: "Erreur lors de l'enregistrement",
    NETWORK_ERROR: "Erreur réseau — vérifiez votre connexion",
    SESSION_EXPIRED: "Session expirée — veuillez vous reconnecter",
  },
});

// Keyboard shortcuts: config/shortcuts.config.js is the single registry
// (refonte WP-17 — the unused KEYBOARD_SHORTCUTS table that stood here gave
// F9 / F12 / F11 meanings the registry and ux/01 §4.2 contradict).
//─── Register ──────────────────────────────────────────────────────────────────
export const WILAYAS = [
  "Adrar",
  "Chlef",
  "Laghouat",
  "Oum El Bouaghi",
  "Batna",
  "Béjaïa",
  "Biskra",
  "Béchar",
  "Blida",
  "Bouira",
  "Tamanrasset",
  "Tébessa",
  "Tlemcen",
  "Tiaret",
  "Tizi Ouzou",
  "Alger",
  "Djelfa",
  "Jijel",
  "Sétif",
  "Saïda",
  "Skikda",
  "Sidi Bel Abbès",
  "Annaba",
  "Guelma",
  "Constantine",
  "Médéa",
  "Mostaganem",
  "MSila",
  "Mascara",
  "Ouargla",
  "Oran",
  "El Bayadh",
  "Illizi",
  "Bordj Bou Arréridj",
  "Boumerdès",
  "El Tarf",
  "Tindouf",
  "Tissemsilt",
  "El Oued",
  "Khenchela",
  "Souk Ahras",
  "Tipaza",
  "Mila",
  "Aïn Defla",
  "Naâma",
  "Aïn Témouchent",
  "Ghardaïa",
  "Relizane",
  "Timimoun",
  "Bordj Badji Mokhtar",
  "Ouled Djellal",
  "Béni Abbès",
  "In Salah",
  "In Guezzam",
  "Touggourt",
  "Djanet",
  "El MGhair",
  "El Menia",
];
export const STEPS = [
  {
    id: 1,
    label: "Pharmacie",
    icon: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z",
  },
  {
    id: 2,
    label: "Compte",
    icon: "M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z",
  },
  {
    id: 3,
    label: "Documents",
    icon: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8",
  },
  { id: 4, label: "Validation", icon: ICONS.check },
];

export const REQUIRED_DOCS = [
  {
    key: "agrement",
    label: "N° Agrément MOH",
    hint: "Délivré par le Ministère de la Santé",
    accept: ".pdf,.jpg,.png",
  },
  {
    key: "registre",
    label: "Registre de commerce",
    hint: "Extrait du registre de commerce en cours",
    accept: ".pdf,.jpg,.png",
  },
  {
    key: "carte_pro",
    label: "Carte professionnelle",
    hint: "Carte de l'Ordre des pharmaciens (en cours)",
    accept: ".pdf,.jpg,.png",
  },
  {
    key: "licence_locale",
    label: "Autorisation communale",
    hint: "Facultatif mais recommandé",
    accept: ".pdf,.jpg,.png",
    optional: true,
  },
];
